import React from "react";
import PropTypes from "prop-types";
import i18n from "i18n/index.js";
import { buildLocalePath, getLocaleFromUrl } from "components/utils/Locale";

function pickLocalized(val) {
  if (val == null || val === "") return "";
  if (typeof val === "string") return val.trim();
  if (typeof val !== "object") return String(val || "").trim();
  const tries = [];
  if (typeof window !== "undefined" && window.I18n && window.I18n.locale) {
    const wl = String(window.I18n.locale);
    tries.push(wl, wl.replace(/-/g, "_"), wl.replace(/_/g, "-"));
    const pw = wl.replace(/-/g, "_").split("_");
    if (pw[0]) tries.push(pw[0], pw[0].toLowerCase());
  }
  const loc = typeof i18n !== "undefined" && i18n.locale ? String(i18n.locale) : "";
  if (loc) {
    tries.push(loc);
    tries.push(loc.toLowerCase(), loc.replace(/_/g, "-"), loc.replace(/-/g, "_"));
    const p = loc.replace(/_/g, "-").split("-");
    if (p[0]) tries.push(p[0], p[0].toLowerCase());
  }
  for (let i = 0; i < tries.length; i += 1) {
    const k = tries[i];
    if (k && val[k] != null && String(val[k]).trim()) return String(val[k]).trim();
  }
  const first = Object.values(val).find((v) => typeof v === "string" && String(v).trim());
  return first ? String(first).trim() : "";
}

function masonryHref(path) {
  if (path == null || path === "") return "#";
  const s = String(path);
  if (/^https?:\/\//i.test(s)) return s;
  const loc = getLocaleFromUrl();
  if (loc && (s === `/${loc}` || s.startsWith(`/${loc}/`))) return s.startsWith("/") ? s : `/${s}`;
  const clean = s.startsWith("/") ? s.slice(1) : s;
  return buildLocalePath(clean);
}

function headingStr(v) {
  if (v == null || v === "") return "";
  return typeof v === "string" ? v.trim() : pickLocalized(v);
}

function railsHomeCollection(props) {
  const r = props.home_collection || props.homeCollection;
  if (!r || typeof r !== "object") return {};
  return {
    ...r,
    product_limit:
      r.product_limit != null ? r.product_limit : r.productLimit,
    collection_type:
      r.collection_type != null ? r.collection_type : r.collectionType,
  };
}

function normalizeMasonryZones(props, hc) {
  const raw =
    props.masonry_zone_groups ??
    props.masonryZoneGroups ??
    hc.masonry_zone_groups ??
    hc.masonryZoneGroups;
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map((g) => ({
    featured: !!(g.featured ?? g.Featured),
    zone_id: g.zone_id ?? g.zoneId,
    zone_name: pickLocalized(g.zone_name ?? g.zoneName ?? ""),
    products_count: Number(g.products_count ?? g.productsCount ?? 0),
    image_url: String(g.image_url ?? g.imageUrl ?? "").trim(),
    path: g.path ?? g.Path ?? "",
  }));
}

function sortFeaturedFirst(groups) {
  return [...groups].sort((a, b) => {
    if (!!a.featured === !!b.featured) return 0;
    return a.featured ? -1 : 1;
  });
}

function MasonrySkeletonGrid({ count }) {
  const n = Math.min(Math.max(count, 1), 5);
  const slots = Math.min(Math.max(count, 1), 8);
  return (
    <>
      <div
        className="home-masonry-destinations home-masonry-destinations--hq"
        data-hq-count={String(n)}
      >
        {Array.from({ length: n }).map((_, idx) => (
          <div
            key={`masonry-sk-${idx}`}
            className={`home-masonry-destinations__card home-masonry-destinations__card--hq${idx} home-masonry-destinations__card--skeleton`}
            aria-hidden
          >
            <div className="oi-skeleton oi-skeleton--wave oi-skeleton--rounded home-masonry-destinations__skeleton-fill" />
          </div>
        ))}
      </div>
      {slots > n ? (
        <div className="home-masonry-destinations home-masonry-destinations--tail">
          {Array.from({ length: slots - n }).map((_, j) => (
            <div
              key={`masonry-sk-tail-${j}`}
              className="home-masonry-destinations__card home-masonry-destinations__card--tail home-masonry-destinations__card--skeleton"
              aria-hidden
            >
              <div className="oi-skeleton oi-skeleton--wave oi-skeleton--rounded home-masonry-destinations__skeleton-fill" />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

function HomeMasonry(props) {
  const hc = railsHomeCollection(props);
  const rawGroups = normalizeMasonryZones(props, hc);
  const groups = sortFeaturedFirst(rawGroups);

  const rawName =
    props.name ??
    props.Name ??
    props.home_collection_title ??
    props.homeCollectionTitle ??
    hc.name ??
    hc.Name ??
    hc.title ??
    hc.Title ??
    "";
  const rawSubtitle =
    props.subtitle ??
    props.Subtitle ??
    props.home_collection_subtitle ??
    props.homeCollectionSubtitle ??
    hc.subtitle ??
    hc.Subtitle ??
    "";

  const name = headingStr(rawName);
  const subtitle = headingStr(rawSubtitle);

  const hcId = hc.id != null ? hc.id : hc["id"];
  const hasId = hcId != null && hcId !== "";
  const hasHeaderSection =
    String(name).trim() !== "" || String(subtitle).trim() !== "" || hasId;

  const limit = Number(hc.product_limit ?? hc.productLimit ?? 8) || 8;
  const skeletonN = Math.min(Math.max(limit, 1), 8);

  const hasZones = groups.length > 0;
  const heroCards = groups.slice(0, Math.min(groups.length, 5));
  const tailCards = groups.slice(5);
  const hqCount = String(Math.min(heroCards.length, 5));

  function renderZoneCard(g, gi, hqIdx) {
    const gid = g.zone_id != null ? String(g.zone_id) : String(gi);
    const featured = g.featured;
    const hqMod =
      hqIdx != null ? ` home-masonry-destinations__card--hq${hqIdx}` : " home-masonry-destinations__card--tail";
    const cardCls =
      `home-masonry-destinations__card${featured ? " home-masonry-destinations__card--featured" : ""}${hqMod}`.trim();
    const bgStyle = g.image_url
      ? {
          backgroundImage: `url("${String(g.image_url).replace(/"/g, '\\"')}")`,
        }
      : undefined;
    const cnt = g.products_count;
    const cntLabel = String(i18n.t("home.masonry_zone_count", { count: cnt })).toUpperCase();

    return (
      <a
        key={`masonry-z-${gid}-${gi}`}
        className={cardCls}
        href={masonryHref(g.path)}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="home-masonry-destinations__bg" style={bgStyle} />
        <span className="home-masonry-destinations__gradient" aria-hidden />
        <span className="home-masonry-destinations__body">
          <span className="home-masonry-destinations__title">{g.zone_name}</span>
          <span className="home-masonry-destinations__count">{cntLabel}</span>
        </span>
      </a>
    );
  }

  const headerBlock =
    hasHeaderSection ? (
      <header className="collections oi-flex oi-flex-column oi-w-full">
        <h2>
          <b>{name}</b> {subtitle}
        </h2>
      </header>
    ) : null;

  return (
    <section className="posts home-masonry-collection oi-flex oi-flex-column oi-w-full">
      {headerBlock}
      <div className="oi-flex oi-flex-column oi-w-full home-masonry-collection__zones">
        {!hasZones ? (
          <MasonrySkeletonGrid count={skeletonN} />
        ) : (
          <>
            <div
              className="home-masonry-destinations home-masonry-destinations--hq"
              data-hq-count={hqCount}
            >
              {heroCards.map((g, i) => renderZoneCard(g, i, i))}
            </div>
            {tailCards.length ? (
              <div className="home-masonry-destinations home-masonry-destinations--tail">
                {tailCards.map((g, j) => renderZoneCard(g, j + 5, null))}
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

HomeMasonry.propTypes = {
  home_collection: PropTypes.object,
  homeCollection: PropTypes.object,
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  home_collection_title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  home_collection_subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  masonry_zone_groups: PropTypes.array,
  masonryZoneGroups: PropTypes.array,
};

export default HomeMasonry;
